import { ReactNode, useMemo, useState } from 'react';
import {
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface DualListBoxProps<T> {
  items: T[];
  selectedIds: string[];
  onChange: (nextSelectedIds: string[]) => void;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  availableTitle: string;
  selectedTitle: string;
  searchPlaceholder?: string;
  emptyText?: string;
}

const ARROW_BUTTON = 'px-3 py-1 border border-gray-300 rounded disabled:opacity-40';

const matchesSearch = (label: string, search: string): boolean =>
  label.toLowerCase().includes(search.trim().toLowerCase());

const toggleId = (ids: string[], id: string): string[] =>
  ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id];

interface PanelProps<T> {
  testId: string;
  title: string;
  items: T[];
  checkedIds: string[];
  onToggle: (id: string) => void;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  emptyText: string;
  search?: ReactNode;
}

function Panel<T>({ testId, title, items, checkedIds, onToggle, getId, getLabel, emptyText, search }: PanelProps<T>) {
  return (
    <Paper
      variant="outlined"
      data-testid={testId}
      className="flex-1 min-w-0 flex flex-col">
      <Typography
        variant="subtitle2"
        className="p-2">
        {title}
      </Typography>
      {search}
      {items.length === 0 ? (
        <Typography
          color="text.secondary"
          className="p-2">
          {emptyText}
        </Typography>
      ) : (
        <List
          dense
          className="overflow-auto"
          sx={{ maxHeight: 320 }}>
          {items.map((item) => {
            const id = getId(item);
            const isChecked = checkedIds.includes(id);
            return (
              <ListItemButton
                key={id}
                selected={isChecked}
                onClick={() => onToggle(id)}>
                <Checkbox
                  edge="start"
                  checked={isChecked}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={getLabel(item)} />
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Paper>
  );
}

export const DualListBox = <T,>({
  items,
  selectedIds,
  onChange,
  getId,
  getLabel,
  availableTitle,
  selectedTitle,
  searchPlaceholder,
  emptyText = 'No items'
}: DualListBoxProps<T>) => {
  const [search, setSearch] = useState('');
  const [checkedAvailable, setCheckedAvailable] = useState<string[]>([]);
  const [checkedSelected, setCheckedSelected] = useState<string[]>([]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const availableItems = useMemo(
    () => items.filter((item) => !selectedSet.has(getId(item)) && matchesSearch(getLabel(item), search)),
    [items, selectedSet, search, getId, getLabel]
  );
  const selectedItems = useMemo(
    () => items.filter((item) => selectedSet.has(getId(item))),
    [items, selectedSet, getId]
  );

  // Only act on checked rows that are still visible, so a search filter can't
  // add/remove a user the admin can no longer see.
  const availableIdSet = useMemo(() => new Set(availableItems.map((item) => getId(item))), [availableItems, getId]);
  const addableIds = useMemo(
    () => checkedAvailable.filter((id) => availableIdSet.has(id)),
    [checkedAvailable, availableIdSet]
  );
  const removableIds = useMemo(
    () => checkedSelected.filter((id) => selectedSet.has(id)),
    [checkedSelected, selectedSet]
  );

  const searchAccessibleName = searchPlaceholder ?? `Search ${availableTitle}`;

  const add = () => {
    onChange([...selectedIds, ...addableIds]);
    setCheckedAvailable([]);
  };

  const remove = () => {
    const removing = new Set(removableIds);
    onChange(selectedIds.filter((id) => !removing.has(id)));
    setCheckedSelected([]);
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="stretch">
      <Panel
        testId="duallistbox-available"
        title={availableTitle}
        items={availableItems}
        checkedIds={checkedAvailable}
        onToggle={(id) => setCheckedAvailable((ids) => toggleId(ids, id))}
        getId={getId}
        getLabel={getLabel}
        emptyText={emptyText}
        search={
          <TextField
            size="small"
            className="mx-2 mb-1"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            inputProps={{ 'aria-label': searchAccessibleName }}
            InputProps={{
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    size="small"
                    edge="end"
                    onClick={() => setSearch('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }}
          />
        }
      />
      <Stack
        spacing={2}
        justifyContent="center"
        alignItems="center">
        <button
          type="button"
          aria-label="Add"
          className={ARROW_BUTTON}
          disabled={addableIds.length === 0}
          onClick={add}>
          &#9654;
        </button>
        <button
          type="button"
          aria-label="Remove"
          className={ARROW_BUTTON}
          disabled={removableIds.length === 0}
          onClick={remove}>
          &#9664;
        </button>
      </Stack>
      <Panel
        testId="duallistbox-selected"
        title={selectedTitle}
        items={selectedItems}
        checkedIds={checkedSelected}
        onToggle={(id) => setCheckedSelected((ids) => toggleId(ids, id))}
        getId={getId}
        getLabel={getLabel}
        emptyText={emptyText}
      />
    </Stack>
  );
};
